import { Box, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { EChartsOption } from "echarts";
import LegendsCharts from "../../legends";
import useStyles from "../styles";
import handleColors from "../utils/handleColors";
import { MyPieDashConfig } from "./types";
import maskUnits from "../../../utils/maskUnits";
import { getColorsArray } from "../../../utils/getColorsPallet";
import calculateMetrics from "../utils/calculateMetrics";

const formatSeries = (data: MyDatum[], indexBy: string, keys: string[]) =>
  data?.map((item) => ({
    name: item[indexBy]?.toString(),
    value: Number(item?.[keys?.[0]] || 0),
  }));

const PieChart: React.FC<MyPieDashConfig> = (props) => {
  const [boxHeight, setBoxHeight] = useState<number | null>(null);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);
  const pieRef = useRef<HTMLDivElement>(null);
  const echartsRef = useRef<ReactECharts>(null);

  const {
    data,
    units,
    fractionDigits,
    keys,
    indexBy,
    metricsType,
    colors,
    colorsByKey,
    subType,
  } = props;

  const theme = useTheme();
  const styles = useStyles(theme);

  useEffect(() => {
    const element = pieRef.current;
    if (!element) return;

    const measure = () => {
      if (element) {
        const { width, height } = element.getBoundingClientRect();
        if (width > 0 && height > 0) {
          setBoxWidth(width);
          setBoxHeight(height);
        }
      }
    };
    measure();

    const timer = setTimeout(measure, 500);

    let resizeTimer: ReturnType<typeof setTimeout>;
    const resizeObserver = new ResizeObserver((entries) => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) return;
          const entry = entries[0];
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0) {
            setBoxWidth(width);
            setBoxHeight(height);
          }
        });
      }, 50);
    });
    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
      clearTimeout(resizeTimer);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (boxWidth && boxHeight && echartsRef.current) {
      const instance = echartsRef.current.getEchartsInstance();
      instance?.resize({ width: boxWidth, height: boxHeight });
    }
  }, [boxWidth, boxHeight]);

  const formattedData = useMemo(
    () => formatSeries(data, indexBy, keys) || [],
    [data, indexBy, keys],
  );

  const newKeysLegends = useMemo(
    () => formattedData.map((item) => item.name),
    [formattedData],
  );

  const chartOption: EChartsOption = useMemo(() => {
    let colorPalette: string[] = [];

    if (colorsByKey) {
      colorPalette = formattedData.map((item) =>
        handleColors(item.name, theme, colorsByKey),
      );
    } else if (colors) {
      colorPalette = getColorsArray(
        formattedData.length,
        colors,
        theme.palette.mode === "dark",
      );
    } else {
      colorPalette = getColorsArray(
        formattedData.length,
        "nivo",
        theme.palette.mode === "dark",
      );
    }

    const isDonut = subType === "donut";
    const minBox = Math.min(boxHeight ?? 0, boxWidth ?? 0);
    const total = formattedData.reduce((sum, item) => sum + item.value, 0);

    return {
      animation: true,
      color: colorPalette,
      tooltip: {
        trigger: "item",
        backgroundColor: theme.palette.background.default,
        textStyle: { color: theme.palette.text.primary },
        confine: true,
        formatter: (params: any) => {
          const value = maskUnits(params.value, units, fractionDigits);
          const percent = params.percent?.toFixed(1);
          return `
            <div style="display: flex; align-items: center; gap: 8px;">
              <div style="width: 10px; height: 10px; border-radius: 50%; background: ${params.color};"></div>
              <div>
                <div style="font-weight: 600;">${params.name}</div>
                <div>${value} (${percent}%)</div>
              </div>
            </div>
          `;
        },
      },
      legend: { show: false },
      series: [
        {
          type: "pie",
          radius: isDonut ? ["70%", "90%"] : ["0%", "90%"],
          center: ["50%", "50%"],
          data: formattedData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
            scaleSize: 8,
          },
          label: {
            show: false,
          },
          labelLine: {
            show: false,
          },
        },
      ],
      graphic: isDonut
        ? {
            type: "group",
            left: "center",
            top: "center",
            children: [
              {
                type: "text",
                style: {
                  text: minBox > 100 ? "Total" : "",
                  textAlign: "center",
                  fill: theme.palette.text.secondary,
                  fontSize: Math.max(12, Math.min(22, minBox * 0.09)),
                  fontWeight: 400,
                },
                top: -minBox * 0.08,
              },
              {
                type: "text",
                style: {
                  text: maskUnits(total, units, fractionDigits),
                  textAlign: "center",
                  fill: theme.palette.text.primary,
                  fontSize: Math.max(16, Math.min(20, minBox * 0.13)),
                  fontWeight: 600,
                },
                top: minBox > 100 ? minBox * 0.08 : 0,
              },
            ],
          }
        : undefined,
    };
  }, [
    formattedData,
    boxWidth,
    boxHeight,
    colors,
    colorsByKey,
    theme,
    units,
    fractionDigits,
    subType,
  ]);

  return formattedData.length === 0 ? undefined : (
    <Box
      sx={{
        ...styles.boxContainer,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          ...styles.boxContainerSizeChart,
          overflow: "hidden",
          minWidth: 0,
          width: "100%",
          flexGrow: 1,
          height: "100%",
          position: "relative",
        }}
        ref={pieRef}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <ReactECharts
            ref={echartsRef}
            option={chartOption}
            style={{ height: "100%", width: "100%" }}
            notMerge={true}
            lazyUpdate={true}
            autoResize={true}
          />
        </Box>
      </Box>
      <Box
        sx={{
          ...styles.boxLegendRadial,
          minHeight: metricsType ? "45px" : "25px",
          flexShrink: 0,
        }}
      >
        <LegendsCharts
          {...props}
          keys={[...(newKeysLegends || [])]}
          position="center"
          metricsType={
            metricsType
              ? calculateMetrics(metricsType, data as any[])
              : undefined
          }
        />
      </Box>
    </Box>
  );
};

export default PieChart;
